import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, TrashIcon, PlusCircleIcon, SlidersHorizontalIcon, WarningIcon, TruckIcon, ArrowRightIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";

const KINDS = [
  // Counter flows
  { v: "refill", l: "Refill · Full Out → Empty In", g: "Counter" },
  { v: "new_connection", l: "New Connection · Full → Full-Out", g: "Counter" },
  { v: "tv_in", l: "TV In · Full → Full-Out", g: "Counter" },
  { v: "surrender", l: "Surrender · Full-Out → Empty", g: "Counter" },
  { v: "tv_out", l: "TV Out · Full-Out → Empty", g: "Counter" },
  // Delivery boy
  { v: "delivery_out", l: "Delivery Boy · Loaded (Full → Boy)", g: "Delivery Boy" },
  { v: "delivery_in", l: "Delivery Confirmed · Refill (Boy → Empty)", g: "Delivery Boy" },
  { v: "delivery_new_connection", l: "Delivery Confirmed · New Connection (Boy → Full-Out)", g: "Delivery Boy" },
  { v: "delivery_return_full", l: "Return Unsold · Boy → Full", g: "Delivery Boy" },
  // Defective
  { v: "defective_in", l: "Defective In · Full → Defective", g: "Defective" },
  { v: "defective_out_to_filled", l: "Defective Out → Full", g: "Defective" },
  { v: "defective_out_to_empty", l: "Defective Out → Empty", g: "Defective" },
  { v: "defective_out_to_full_out", l: "Defective Out → Full-Out", g: "Defective" },
  // Manual (highlighted)
  { v: "manual_filled_in", l: "Manual · Filled In (reason required)", g: "Manual" },
  { v: "manual_filled_out", l: "Manual · Filled Out (reason required)", g: "Manual" },
  { v: "manual_empty_in", l: "Manual · Empty In (reason required)", g: "Manual" },
  { v: "manual_empty_out", l: "Manual · Empty Out (reason required)", g: "Manual" },
];

const DISPLAY_LABEL = {
  ...Object.fromEntries([
    ["refill","Refill · Full Out → Empty In"],
    ["new_connection","New Connection · Full → Full-Out"],
    ["tv_in","TV In · Full → Full-Out"],
    ["surrender","Surrender · Full-Out → Empty"],
    ["tv_out","TV Out · Full-Out → Empty"],
    ["commercial_out","Commercial Delivery · Filled Out (auto)"],
    ["commercial_in","Commercial Delivery · Empty In (auto)"],
    ["delivery_out","Delivery Boy · Loaded (Full → Boy)"],
    ["delivery_in","Delivery Confirmed · Refill (Boy → Empty)"],
    ["delivery_new_connection","Delivery Confirmed · New Connection (Boy → Full-Out)"],
    ["delivery_return_full","Return Unsold · Boy → Full"],
    ["defective_in","Defective In · Full → Defective"],
    ["defective_out_to_filled","Defective Out → Full"],
    ["defective_out_to_empty","Defective Out → Empty"],
    ["defective_out_to_full_out","Defective Out → Full-Out"],
    ["manual_filled_in","Manual · Filled In (reason required)"],
    ["manual_filled_out","Manual · Filled Out (reason required)"],
    ["manual_empty_in","Manual · Empty In (reason required)"],
    ["manual_empty_out","Manual · Empty Out (reason required)"],
    ["plant_in","Plant Receipt · Filled In"],
    ["plant_out_empty","Plant Receipt · Empty Out"],
    ["adjustment","Adjustment / Opening Balance"],
  ]),
};

const MANUAL_KINDS = new Set([
  "manual_filled_in", "manual_filled_out", "manual_empty_in", "manual_empty_out",
]);

export default function Cylinders() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [types, setTypes] = useState([]);
  const [stock, setStock] = useState([]);
  const [txns, setTxns] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const reload = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/stock"),
        api.get(`/transactions?date=${date}`),
      ]);
      setStock(s.data);
      setTypes(s.data);
      setTxns(t.data);
    } catch {
      toast.error("Failed to load");
    }
  }, [date]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-10" data-testid="cylinders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Stock control</div>
          <h1 className="font-display text-4xl font-black">Cylinder Stock</h1>
          <p className="text-slate-500 mt-2 max-w-lg">Log every movement — refill, new connection, surrender, delivery-boy and defective — per cylinder type.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isOwner && <AddTypeDialog onDone={reload} />}
          {isOwner && <BulkRatesDialog types={types} onDone={reload} />}
          {can(user, "cylinders:write") && <PlantReceiptDialog types={types} onDone={reload} />}
          <AddTxnDialog types={types} onDone={reload} />
        </div>
      </div>

      {/* Stock grid */}
      <section>
        <div className="overline text-slate-500 mb-3">Current Stock</div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider">Cylinder Type</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Category</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Full</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Empty</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Defective</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Rate ₹</TableHead>
                {isOwner && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {stock.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`stock-row-${t.id}`}>
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      {t.name}
                      {t.low_stock && (
                        <span className="inline-flex items-center gap-1 border border-[#FF2400] text-[#FF2400] px-1.5 py-0.5 text-xs uppercase tracking-wider" data-testid={`low-stock-${t.id}`}>
                          <WarningIcon size={10} weight="bold" /> Low
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">min full: {t.min_full || 0}</div>
                  </TableCell>
                  <TableCell>
                    <span className="overline" style={{ color: t.category === "domestic" ? "#FF2400" : "#002FA7" }}>{t.category}</span>
                  </TableCell>
                  <TableCell className={`text-right font-mono-num font-semibold ${t.low_stock ? "text-[#FF2400]" : ""}`}>{t.stock.full}</TableCell>
                  <TableCell className="text-right font-mono-num">{t.stock.empty}</TableCell>
                  <TableCell className="text-right font-mono-num">{t.stock.defective}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-brand-blue" data-testid={`rate-${t.id}`}>
                    {t.default_rate > 0 ? `₹${Number(t.default_rate).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                  </TableCell>
                  {isOwner && (
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <AdjustDialog t={t} onDone={reload} />
                        <button
                          onClick={async () => {
                            if (!confirm(`Delete "${t.name}" and its transactions?`)) return;
                            try { await api.delete(`/cylinder-types/${t.id}`); toast.success("Deleted"); reload(); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                          }}
                          className="text-slate-400 hover:text-[#FF2400]"
                          data-testid={`delete-type-${t.id}`}
                        >
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {stock.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No cylinder types yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Movements */}
      <section>
        <OpeningClosingReport />
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Movements</div>
          <div className="flex items-center gap-2">
            <Label htmlFor="txn-date" className="overline">Date</Label>
            <Input
              id="txn-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40 h-9 rounded-none border-slate-300"
              data-testid="txn-date-filter"
            />
          </div>
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider">Kind</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Cylinder</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Qty</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Delivery Boy</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Invoices</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Note</TableHead>
                {isOwner && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {txns.map((t) => {
                const type = types.find((x) => x.id === t.cylinder_type_id);
                const isManual = ["manual_filled_in","manual_filled_out","manual_empty_in","manual_empty_out"].includes(t.kind);
                const kindLabel = t.kind === "adjustment"
                  ? "Adjustment"
                  : (DISPLAY_LABEL[t.kind] || t.kind);
                const qtyDisplay = t.kind === "adjustment"
                  ? Object.entries(t.delta || {}).map(([k, v]) => `${k}:${v > 0 ? "+" : ""}${v}`).join(" · ") || "0"
                  : t.quantity;
                return (
                  <TableRow key={t.id} className={`hover:bg-slate-50 border-b border-slate-100 ${isManual ? "bg-amber-50/40" : ""}`} data-testid={`txn-row-${t.id}`}>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        {isManual && <span className="inline-block w-1 h-4 bg-amber-500" aria-hidden="true" />}
                        <span>{kindLabel}</span>
                      </div>
                    </TableCell>
                    <TableCell>{type?.name || "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold text-xs">{qtyDisplay}</TableCell>
                    <TableCell className="text-slate-600">{t.delivery_boy || "—"}</TableCell>
                    <TableCell className="text-slate-600 font-mono-num text-xs">{t.invoices_verified ?? "—"}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {t.customer_id ? (
                        <Link to={`/customers/${t.customer_id}`} className="text-brand-blue hover:text-brand-orange-ink inline-flex items-center gap-1 font-semibold" data-testid={`txn-customer-link-${t.id}`}>
                          {t.customer_name || "Customer"}
                          <ArrowRightIcon size={10} />
                        </Link>
                      ) : (
                        t.note || (isManual ? <span className="text-[#FF2400]">reason missing</span> : "—")
                      )}
                    </TableCell>
                    {isOwner && (
                      <TableCell>
                        <button
                          onClick={async () => {
                            if (!confirm("Delete this movement?")) return;
                            try { await api.delete(`/transactions/${t.id}`); toast.success("Deleted"); reload(); }
                            catch { toast.error("Failed"); }
                          }}
                          className="text-slate-400 hover:text-[#FF2400]"
                          data-testid={`delete-txn-${t.id}`}
                        >
                          <TrashIcon size={16} />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {txns.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No movements for this date.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

function AddTypeDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [size, setSize] = useState("14.2");
  const [cat, setCat] = useState("domestic");
  const [minFull, setMinFull] = useState("0");
  const [rate, setRate] = useState("0");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cylinder-types", {
        name, size_kg: parseFloat(size), category: cat,
        min_full: parseInt(minFull, 10) || 0,
        default_rate: parseFloat(rate) || 0,
      });
      toast.success("Cylinder type added");
      setOpen(false); setName(""); setSize("14.2"); setCat("domestic"); setMinFull("0"); setRate("0");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-slate-900" data-testid="add-type-button">
          <PlusCircleIcon size={16} className="mr-2" /> Add Type
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display">New cylinder type</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-type-form">
          <div>
            <Label className="overline">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none" data-testid="type-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Size (kg)</Label>
              <Input type="number" step="0.1" value={size} onChange={(e) => setSize(e.target.value)} required className="rounded-none" data-testid="type-size-input" />
            </div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="rounded-none" data-testid="type-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">Domestic</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Low-Stock at</Label>
              <Input type="number" min="0" value={minFull} onChange={(e) => setMinFull(e.target.value)} className="rounded-none font-mono-num" data-testid="type-min-full-input" />
            </div>
            <div>
              <Label className="overline">Default rate ₹</Label>
              <Input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="type-default-rate-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="type-submit-button">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ t, onDone }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(t.stock.full);
  const [empty, setEmpty] = useState(t.stock.empty);
  const [defective, setDefective] = useState(t.stock.defective);
  const [out, setOut] = useState(t.stock.out_with_boy);
  const [fullOut, setFullOut] = useState(t.stock.full_out || 0);
  const [minFull, setMinFull] = useState(t.min_full || 0);
  const [note, setNote] = useState("Opening balance");

  useEffect(() => {
    if (open) {
      setFull(t.stock.full); setEmpty(t.stock.empty);
      setDefective(t.stock.defective); setOut(t.stock.out_with_boy);
      setFullOut(t.stock.full_out || 0); setMinFull(t.min_full || 0);
    }
  }, [open, t]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (parseInt(minFull, 10) !== (t.min_full || 0)) {
        await api.patch(`/cylinder-types/${t.id}`, { min_full: parseInt(minFull, 10) });
      }
      await api.post(`/stock/${t.id}/adjust`, {
        full: parseInt(full, 10),
        empty: parseInt(empty, 10),
        defective: parseInt(defective, 10),
        out_with_boy: parseInt(out, 10),
        full_out: parseInt(fullOut, 10),
        note,
      });
      toast.success("Stock adjusted");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-400 hover:text-slate-900" data-testid={`adjust-type-${t.id}`} title="Adjust / opening balance">
          <SlidersHorizontalIcon size={16} />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Adjust · {t.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="adjust-form">
          <p className="text-xs text-slate-500 border-l-2 border-slate-900 pl-3">
            Set absolute counts. An adjustment entry is recorded for auditability.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Full</Label>
              <Input type="number" min="0" value={full} onChange={(e) => setFull(e.target.value)} required className="rounded-none font-mono-num" data-testid="adjust-full" />
            </div>
            <div>
              <Label className="overline">Empty</Label>
              <Input type="number" min="0" value={empty} onChange={(e) => setEmpty(e.target.value)} required className="rounded-none font-mono-num" data-testid="adjust-empty" />
            </div>
            <div>
              <Label className="overline">With Boy</Label>
              <Input type="number" min="0" value={out} onChange={(e) => setOut(e.target.value)} required className="rounded-none font-mono-num" data-testid="adjust-out" />
            </div>
            <div>
              <Label className="overline">Full-Out (at customer)</Label>
              <Input type="number" min="0" value={fullOut} onChange={(e) => setFullOut(e.target.value)} required className="rounded-none font-mono-num" data-testid="adjust-full-out" />
            </div>
            <div>
              <Label className="overline">Defective</Label>
              <Input type="number" min="0" value={defective} onChange={(e) => setDefective(e.target.value)} required className="rounded-none font-mono-num" data-testid="adjust-defective" />
            </div>
            <div>
              <Label className="overline">Low-Stock at</Label>
              <Input type="number" min="0" value={minFull} onChange={(e) => setMinFull(e.target.value)} className="rounded-none font-mono-num" data-testid="adjust-min-full" />
            </div>
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="adjust-note" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="adjust-submit-button">Apply</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddTxnDialog({ types, onDone }) {
  const [open, setOpen] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [d, setD] = useState(new Date().toISOString().slice(0, 10));
  // Shared fields — used by rows that need them
  const [boy, setBoy] = useState("");
  const [method, setMethod] = useState("boy");
  const [invoiceNo, setInvoiceNo] = useState("");
  // Multi-row entries
  const emptyRow = () => ({ kind: "refill", qty: "1", note: "" });
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!typeId && types[0]) setTypeId(types[0].id); }, [types, typeId]);
  useEffect(() => { if (open) setRows([emptyRow()]); }, [open]);

  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const rowNeedsBoy = (k) => ["delivery_out", "delivery_in", "delivery_new_connection", "delivery_return_full"].includes(k);
  const rowNeedsMethod = (k) => ["refill", "new_connection", "delivery_in", "delivery_new_connection"].includes(k);
  const rowNeedsInvoiceNo = rowNeedsMethod;
  const anyRowNeedsBoy = rows.some((r) => rowNeedsBoy(r.kind));
  const anyRowNeedsMethod = rows.some((r) => rowNeedsMethod(r.kind));

  const groups = ["Counter", "Delivery Boy", "Defective", "Manual"];

  const submit = async (e) => {
    e.preventDefault();
    if (!typeId) return toast.error("Select a cylinder type");
    if (rows.length === 0) return toast.error("Add at least one movement row");
    // Validation
    for (const r of rows) {
      const q = parseInt(r.qty, 10) || 0;
      if (q <= 0) return toast.error("Every row needs a quantity ≥ 1");
      if (MANUAL_KINDS.has(r.kind) && !(r.note || "").trim())
        return toast.error(`Manual "${DISPLAY_LABEL[r.kind]}" needs a reason`);
    }
    if (anyRowNeedsBoy && !boy.trim()) return toast.error("Delivery-boy name is required for delivery rows");
    setSaving(true);
    let ok = 0, fail = 0;
    for (const r of rows) {
      const payload = {
        cylinder_type_id: typeId,
        kind: r.kind,
        quantity: parseInt(r.qty, 10),
        delivery_boy: rowNeedsBoy(r.kind) ? boy : "",
        note: r.note || "",
        date: d,
      };
      if (rowNeedsMethod(r.kind)) payload.delivery_method = method;
      if (rowNeedsInvoiceNo(r.kind) && invoiceNo) payload.invoice_no = invoiceNo;
      try {
        await api.post("/transactions", payload);
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${DISPLAY_LABEL[r.kind]} · ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setSaving(false);
    if (ok > 0) toast.success(`Recorded ${ok} movement${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
    if (fail === 0) {
      setOpen(false);
      setRows([emptyRow()]); setBoy(""); setInvoiceNo("");
      onDone();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-slate-900 hover:bg-black text-white" data-testid="add-txn-button">
          <PlusIcon size={16} className="mr-2" /> Log Movement
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">Log cylinder movements</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-txn-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Cylinder type</Label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger className="rounded-none" data-testid="txn-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="txn-date-input" />
            </div>
          </div>

          <div className="border border-slate-200">
            <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider px-3 py-2 text-slate-500">
              <div className="col-span-5">Movement kind</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-4">Note / reason</div>
              <div className="col-span-1"></div>
            </div>
            {rows.map((r, i) => (
              <div key={r.id || `row-${i}`} className="grid grid-cols-12 border-b border-slate-100 px-3 py-2 items-center gap-2" data-testid={`txn-row-${i}`}>
                <div className="col-span-5">
                  <Select value={r.kind} onValueChange={(v) => setRow(i, { kind: v })}>
                    <SelectTrigger className="rounded-none h-9" data-testid={`txn-kind-select-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[360px]">
                      {groups.map((g) => (
                        <div key={g}>
                          <div className="px-2 py-1 text-xs uppercase tracking-wider text-slate-400 bg-slate-50">{g}</div>
                          {KINDS.filter((k) => k.g === g).map((k) => (
                            <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  type="number" min="1" value={r.qty}
                  onChange={(e) => setRow(i, { qty: e.target.value })}
                  className="col-span-2 rounded-none font-mono-num text-right h-9"
                  data-testid={`txn-qty-input-${i}`}
                />
                <Input
                  value={r.note}
                  onChange={(e) => setRow(i, { note: e.target.value })}
                  className="col-span-4 rounded-none h-9"
                  placeholder={MANUAL_KINDS.has(r.kind) ? "Reason required" : "Optional"}
                  required={MANUAL_KINDS.has(r.kind)}
                  data-testid={`txn-note-input-${i}`}
                />
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    disabled={rows.length === 1}
                    className="text-slate-400 hover:text-[#FF2400] disabled:opacity-30 disabled:hover:text-slate-400"
                    data-testid={`txn-row-remove-${i}`}
                    title="Remove row"
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              </div>
            ))}
            <div className="px-3 py-2">
              <button
                type="button"
                onClick={addRow}
                className="text-xs uppercase tracking-wider text-slate-900 hover:text-brand-orange-ink inline-flex items-center gap-1"
                data-testid="txn-add-row"
              >
                <PlusCircleIcon size={14} /> Add another movement
              </button>
            </div>
          </div>

          {anyRowNeedsBoy && (
            <div>
              <Label className="overline">Delivery boy (applies to delivery rows)</Label>
              <Input value={boy} onChange={(e) => setBoy(e.target.value)} required className="rounded-none" data-testid="txn-boy-input" />
            </div>
          )}
          {anyRowNeedsMethod && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Delivery Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger className="rounded-none" data-testid="txn-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boy">Delivery Boy</SelectItem>
                    <SelectItem value="cash_carry">Cash &amp; Carry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Invoice no.</Label>
                <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} className="rounded-none font-mono-num" placeholder="e.g. INV-1024" data-testid="txn-invoice-no-input" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-none bg-slate-900 hover:bg-black disabled:opacity-50" data-testid="txn-submit-button">
              {saving ? "Recording…" : `Record ${rows.length} movement${rows.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlantReceiptDialog({ types, onDone }) {
  const [open, setOpen] = useState(false);
  const [voucher, setVoucher] = useState("");
  const [d, setD] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setRows(types.map((t) => ({ cylinder_type_id: t.id, name: t.name, filled_in: "", empty_out: "" })));
    }
  }, [open, types]);

  const submit = async (e) => {
    e.preventDefault();
    const items = rows
      .map((r) => ({
        cylinder_type_id: r.cylinder_type_id,
        filled_in: parseInt(r.filled_in || 0, 10),
        empty_out: parseInt(r.empty_out || 0, 10),
      }))
      .filter((r) => r.filled_in > 0 || r.empty_out > 0);
    if (items.length === 0) return toast.error("Enter at least one non-zero row");
    try {
      await api.post("/plant-receipt", { date: d, voucher_no: voucher, note, items });
      toast.success(`Plant receipt saved (${items.length} rows)`);
      setOpen(false); setVoucher(""); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const update = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white" data-testid="plant-receipt-button">
          <TruckIcon size={16} className="mr-2" /> Plant Receipt
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Plant Receipt · Voucher</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="plant-receipt-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Voucher no.</Label>
              <Input value={voucher} onChange={(e) => setVoucher(e.target.value)} className="rounded-none font-mono-num" placeholder="e.g. 1024" data-testid="plant-voucher-input" />
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="plant-date-input" />
            </div>
          </div>
          <div className="border border-slate-200">
            <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider px-3 py-2 text-slate-500">
              <div className="col-span-6">Cylinder Type</div>
              <div className="col-span-3 text-right">Filled IN (from plant)</div>
              <div className="col-span-3 text-right">Empty OUT (to plant)</div>
            </div>
            {rows.map((r, i) => (
              <div key={r.cylinder_type_id} className="grid grid-cols-12 border-b border-slate-100 px-3 py-2 items-center" data-testid={`plant-row-${i}`}>
                <div className="col-span-6 text-sm font-semibold">{r.name}</div>
                <Input type="number" min="0" value={r.filled_in} onChange={(e) => update(i, "filled_in", e.target.value)}
                  className="col-span-3 rounded-none font-mono-num text-right h-9" data-testid={`plant-filled-${i}`} />
                <Input type="number" min="0" value={r.empty_out} onChange={(e) => update(i, "empty_out", e.target.value)}
                  className="col-span-3 rounded-none font-mono-num text-right h-9" data-testid={`plant-empty-${i}`} />
              </div>
            ))}
            {rows.length === 0 && (
              <div className="text-center text-slate-400 py-6 text-sm">Add a cylinder type first.</div>
            )}
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="plant-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="plant-submit-button">Save Receipt</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function OpeningClosingReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/opening-closing-stock?from_date=${from}&to_date=${to}`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const downloadCSV = () => {
    const headers = ["Product","Category","Opening Full","Opening Empty","Opening Defective","Closing Full","Closing Empty","Closing Defective","Δ Full","Δ Empty"];
    const csv = [headers.join(",")]
      .concat(rows.map(r => [
        `"${r.name}"`, `"${r.category || ""}"`,
        r.opening_full, r.opening_empty, r.opening_def,
        r.closing_full, r.closing_empty, r.closing_def,
        r.delta_full, r.delta_empty,
      ].join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `opening-closing-stock_${from}_to_${to}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="border-2 border-brand-blue bg-white p-4 mb-6" data-testid="opening-closing-report">
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <div className="overline text-brand-orange-ink">Report</div>
          <h3 className="font-display text-xl font-black text-brand-blue">Opening / Closing Stock</h3>
        </div>
        <div className="flex-1" />
        <div>
          <Label className="overline">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40 h-9" data-testid="oc-from" />
        </div>
        <div>
          <Label className="overline">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40 h-9" data-testid="oc-to" />
        </div>
        <Button onClick={downloadCSV} disabled={!rows.length}
          className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white h-9" data-testid="oc-download-csv">
          <DownloadSimpleIcon size={14} className="mr-2" /> CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blue-tint">
            <tr>
              <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue">Product</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Opening Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Opening Empty</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Closing Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Closing Empty</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Δ Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Δ Empty</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center text-slate-400 py-4">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-4">No data.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`oc-row-${r.id}`}>
                <td className="p-2 font-semibold">{r.name}</td>
                <td className="p-2 text-right font-mono-num">{r.opening_full}</td>
                <td className="p-2 text-right font-mono-num">{r.opening_empty}</td>
                <td className="p-2 text-right font-mono-num font-semibold">{r.closing_full}</td>
                <td className="p-2 text-right font-mono-num font-semibold">{r.closing_empty}</td>
                <td className={`p-2 text-right font-mono-num ${r.delta_full > 0 ? "text-emerald-700" : r.delta_full < 0 ? "text-[#FF2400]" : "text-slate-500"}`}>{r.delta_full > 0 ? "+" : ""}{r.delta_full}</td>
                <td className={`p-2 text-right font-mono-num ${r.delta_empty > 0 ? "text-emerald-700" : r.delta_empty < 0 ? "text-[#FF2400]" : "text-slate-500"}`}>{r.delta_empty > 0 ? "+" : ""}{r.delta_empty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function BulkRatesDialog({ types, onDone }) {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    (types || []).forEach((t) => { seed[t.id] = String(t.default_rate ?? 0); });
    setRates(seed);
  }, [open, types]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const items = Object.entries(rates).map(([id, v]) => ({ id, default_rate: parseFloat(v) || 0 }));
      const r = await api.post("/cylinder-types/bulk-rates", { items });
      toast.success(`Updated ${r.data.updated} of ${r.data.requested} rate${r.data.requested > 1 ? "s" : ""}`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-brand-orange text-brand-orange-ink hover:bg-brand-orange-tint" data-testid="bulk-rates-button">
          <SlidersHorizontalIcon size={16} className="mr-2" /> Update Rates
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Monthly rate revision</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="bulk-rates-form">
          <p className="text-xs text-slate-500 uppercase tracking-wider">
            Only affects future deliveries. Existing entries stay locked at their historical rate.
          </p>
          <div className="border border-slate-200 divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
            {(types || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-4 p-3" data-testid={`bulk-rate-row-${t.id}`}>
                <div>
                  <div className="font-semibold text-brand-blue">{t.name}</div>
                  <div className="overline text-slate-400" style={{ fontSize: 10 }}>{t.category} · {t.size_kg} kg</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 font-mono-num text-sm">₹</span>
                  <Input
                    type="number" step="0.01" min="0"
                    value={rates[t.id] ?? ""}
                    onChange={(e) => setRates((r) => ({ ...r, [t.id]: e.target.value }))}
                    className="rounded-none w-32 font-mono-num text-right"
                    data-testid={`bulk-rate-input-${t.id}`}
                  />
                </div>
              </div>
            ))}
            {(!types || types.length === 0) && (
              <div className="p-6 text-center text-slate-400 text-sm">Add a cylinder type first.</div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving || !types?.length} className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="bulk-rates-save">
              {saving ? "Saving…" : "Save all rates"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
